// src/services/dashboardService.js
import {
  collection,
  query,
  where,
  getDocs,
  Timestamp,
  getCountFromServer,
  orderBy,
  limit,
} from "firebase/firestore";
import { db } from "./firebaseConfig";

export const fetchTotalReservasNoMes = async () => {
  const hoje = new Date();
  const primeiroDiaDoMes = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
  const ultimoDiaDoMes = new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0);

  const reservationsRef = collection(db, "reservations");

  const q = query(
    reservationsRef,
    where("checkInDate", ">=", Timestamp.fromDate(primeiroDiaDoMes)),
    where("checkInDate", "<=", Timestamp.fromDate(ultimoDiaDoMes)),
  );

  const snapshot = await getCountFromServer(q);
  return snapshot.data().count;
};

export const fetchGuiasAtivosNoMes = async (month, year) => {
  const hoje = new Date();
  const targetMonth = month !== undefined ? month : hoje.getMonth();
  const targetYear = year !== undefined ? year : hoje.getFullYear();
  const primeiroDiaDoMes = new Date(targetYear, targetMonth, 1);
  const ultimoDiaDoMes = new Date(targetYear, targetMonth + 1, 0);

  const excursionsRef = collection(db, "excursions");
  const q = query(
    excursionsRef,
    where("excursionDate", ">=", Timestamp.fromDate(primeiroDiaDoMes)),
    where("excursionDate", "<=", Timestamp.fromDate(ultimoDiaDoMes)),
  );

  const [guiasSnapshot, excursionsSnapshot] = await Promise.all([
    getDocs(collection(db, "guias")),
    getDocs(q)
  ]);
  
  const guiasCount = {};
  
  // 1. Inicializa todos os guias cadastrados com 0
  guiasSnapshot.forEach(doc => {
    guiasCount[doc.data().name] = 0;
  });

  // 2. Conta as excursões
  excursionsSnapshot.forEach(doc => {
    const data = doc.data();
    if (data.isGuide && data.organizerName) {
      const nomeGuia = data.organizerName;
      if (guiasCount[nomeGuia] === undefined) {
        guiasCount[nomeGuia] = 0;
      }
      guiasCount[nomeGuia]++;
    }
  });

  return Object.keys(guiasCount)
    .map(nome => ({
      name: nome,
      count: guiasCount[nome]
    }))
    .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
};

export const fetchTotalExcursoesNoMes = async () => {
  const hoje = new Date();
  const primeiroDiaDoMes = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
  const ultimoDiaDoMes = new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0);

  const excursionsRef = collection(db, "excursions");

  const q = query(
    excursionsRef,

    where("excursionDate", ">=", Timestamp.fromDate(primeiroDiaDoMes)),
    where("excursionDate", "<=", Timestamp.fromDate(ultimoDiaDoMes)),
  );

  const snapshot = await getCountFromServer(q);
  return snapshot.data().count;
};

export const fetchUpcomingPayments = async () => {
  const reservationsRef = collection(db, "reservations");

  const q = query(
    reservationsRef,
    where("paymentStatus", "==", "pending"),
    orderBy("depositDueDate", "asc"),
    limit(5),
  );

  const querySnapshot = await getDocs(q);
  const payments = [];
  querySnapshot.forEach((doc) => {
    const data = doc.data();
    if (data.depositDueDate) {
      payments.push({
        id: doc.id,
        guestInfo: data.guestInfo,
        phoneInfo: data.phoneInfo,
        chaletNumber: data.chaletNumber,
        depositDueDate: data.depositDueDate.toDate(),
      });
    }
  });
  return payments;
};

export const fetchExcursoesConfirmadas = async () => {
  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);

  const excursionsRef = collection(db, "excursions");

  const q = query(
    excursionsRef,
    where("status", "==", "Confirmado"),
    where("excursionDate", ">=", Timestamp.fromDate(hoje)),
    orderBy("excursionDate", "asc"),
    limit(50), // Fetch next 50 to guarantee we get at least 5 distinct dates
  );

  const querySnapshot = await getDocs(q);

  const excursionsByDay = {};
  const weekDays = [
    "Domingo",
    "Segunda",
    "Terça",
    "Quarta",
    "Quinta",
    "Sexta",
    "Sábado",
  ];

  querySnapshot.forEach((doc) => {
    const data = doc.data();
    const date = data.excursionDate.toDate();
    const dayOfWeek = weekDays[date.getDay()];
    const dateString = `${dayOfWeek} (${date.toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
    })})`;

    if (!excursionsByDay[dateString]) {
      excursionsByDay[dateString] = {
        label: dateString,
        count: 0,
        date: date,
      };
    }
    excursionsByDay[dateString].count++;
  });

  const result = Object.values(excursionsByDay)
    .sort((a, b) => a.date - b.date)
    .slice(0, 5);

  return result;
};

/**
 * Busca e agrupa o total de excursões por mês dentro de um período de datas.
 * @param {Date} startDate - A data de início do período.
 * @param {Date} endDate - A data de fim do período.
 * @returns {Array<{name: string, total: number}>} - Um array de dados para o gráfico.
 */
export const fetchExcursoesPorPeriodo = async (startDate, endDate) => {
  if (!startDate || !endDate) {
    return [];
  }

  const excursionsRef = collection(db, "excursions");
  const q = query(
    excursionsRef,
    where("excursionDate", ">=", Timestamp.fromDate(startDate)),
    where("excursionDate", "<=", Timestamp.fromDate(endDate)),
  );

  const querySnapshot = await getDocs(q);
  const monthlyCounts = {};

  querySnapshot.forEach((doc) => {
    const data = doc.data();
    const date = data.excursionDate.toDate();
    const year = date.getFullYear();
    const month = date.getMonth();

    const monthKey = `${year}-${month}`;

    if (!monthlyCounts[monthKey]) {
      monthlyCounts[monthKey] = {
        name: date
          .toLocaleDateString("pt-BR", { month: "short", year: "2-digit" })
          .replace(".", ""),
        total: 0,
        sortKey: `${year}-${String(month).padStart(2, "0")}`,
      };
    }
    monthlyCounts[monthKey].total++;
  });

  const chartData = Object.values(monthlyCounts);

  chartData.sort((a, b) => a.sortKey.localeCompare(b.sortKey));

  return chartData;
};

export const fetchAvailableYears = async () => {
  const excursionsRef = collection(db, "excursions");
  const snapshot = await getDocs(excursionsRef);
  const years = new Set();
  
  snapshot.forEach(doc => {
    const data = doc.data();
    if (data.excursionDate && data.isGuide) {
      const date = data.excursionDate.toDate();
      years.add(date.getFullYear());
    }
  });

  const currentYear = new Date().getFullYear();
  years.add(currentYear);

  return Array.from(years).sort((a, b) => a - b);
};
